import { checkInOpen } from "./check-in.ts";
import { DEFAULT_TIMEZONE, startOfLocalDay } from "./time.ts";

/**
 * Attendance rules, in one place for the API, the web and the mobile app (SPECS.md, "Scope since
 * 2026-09-25").
 *
 * Attendance is a record, not a second deduction. A session comes off the balance when it is
 * booked, so marking present or absent, or clearing a mark, never moves a balance.
 */

/**
 * Marks open on the session's own day in the business's clock, and stay open after it. They also
 * open when check-in opens (15 minutes before the start), so a class just after midnight takes its
 * check-in taps before the day turns (internal review R3.1).
 */
export function attendanceOpen(sessionStartsAt: Date, now: Date, timeZone: string = DEFAULT_TIMEZONE): boolean {
  return startOfLocalDay(sessionStartsAt, timeZone).getTime() <= now.getTime() || checkInOpen(sessionStartsAt, now);
}

/** One booked participant on the attendance screen: the booking and its mark, null until marked. */
export type AttendanceEntry = { bookingId: string; attendance?: "present" | "absent" | null };

/**
 * Attendance is taken when every booked participant has a mark. A session with nobody booked has
 * nothing to take. A register marked half way through (the older one-row-at-a-time screen) is not
 * taken yet.
 */
export function attendanceTaken(roster: readonly AttendanceEntry[]): boolean {
  return roster.length > 0 && roster.every((entry) => entry.attendance === "present" || entry.attendance === "absent");
}

/** Came and absent counts, for "8 came, 2 absent". Unmarked rows count in neither. */
export function attendanceCounts(roster: readonly AttendanceEntry[]): { present: number; absent: number } {
  let present = 0;
  let absent = 0;
  for (const entry of roster) {
    if (entry.attendance === "present") present++;
    else if (entry.attendance === "absent") absent++;
  }
  return { present, absent };
}

/**
 * One session's register: the booked participants with their marks, and the names the coach added
 * who came and wait for a parent (`waitingExtras`, migration 0038). Those have no booking yet, so the
 * roster leaves them out.
 */
export type SessionRegister = { roster: readonly AttendanceEntry[]; waitingExtras?: readonly unknown[] };

/**
 * Came and absent for a whole session, for "9 came, 0 absent": the booked marks plus every waiting
 * name, which counts as Came. The roster alone said "2 came" for a level where 9 swam (pilot, 2026-10-22).
 */
export function sessionAttendanceCounts(session: SessionRegister): { present: number; absent: number } {
  const counts = attendanceCounts(session.roster);
  return { present: counts.present + (session.waitingExtras?.length ?? 0), absent: counts.absent };
}

/**
 * Attendance of a whole session is taken when every booked participant has a mark, or, with nobody
 * booked, when the coach saved the names of the people who came.
 */
export function sessionAttendanceTaken(session: SessionRegister): boolean {
  return registerTaken({
    booked: session.roster.length,
    unmarked: session.roster.filter((entry) => entry.attendance !== "present" && entry.attendance !== "absent").length,
    waiting: session.waitingExtras?.length ?? 0,
  });
}

/**
 * The same rule as `sessionAttendanceTaken`, from counts: for a report that counts in the database
 * and has no roster. `booked` is the confirmed bookings, `unmarked` the ones with no mark, `waiting`
 * the names that wait for a parent.
 */
export function registerTaken(counts: { booked: number; unmarked: number; waiting: number }): boolean {
  if (counts.booked === 0) return counts.waiting > 0;
  return counts.unmarked === 0;
}

/**
 * The marks to send when a person saves: only the bookings whose answer differs from what is
 * stored, so a second save with no change sends nothing and writes no audit row. Each mark carries
 * `expected`, the mark the screen read, so the API refuses to write over a change made since.
 */
export function changedMarks(
  roster: readonly AttendanceEntry[],
  answers: Readonly<Record<string, "present" | "absent">>,
): Array<{ bookingId: string; attendance: "present" | "absent"; expected: "present" | "absent" | null }> {
  const marks: Array<{ bookingId: string; attendance: "present" | "absent"; expected: "present" | "absent" | null }> = [];
  for (const entry of roster) {
    const answer = answers[entry.bookingId];
    const stored = entry.attendance ?? null;
    if (answer && answer !== stored) marks.push({ bookingId: entry.bookingId, attendance: answer, expected: stored });
  }
  return marks;
}

/**
 * The answers on the attendance screen: the person's own edits over the marks stored now. A
 * participant the person did not touch shows the stored mark, so a refetch brings in another
 * device's change. A `null` edit is a cleared answer (Undo, Change). Desk and the mobile app use
 * this same rule.
 */
export function resolveAnswers(
  edits: Readonly<Record<string, "present" | "absent" | null>>,
  roster: readonly AttendanceEntry[],
): Record<string, "present" | "absent"> {
  const answers: Record<string, "present" | "absent"> = {};
  for (const entry of roster) {
    const value = Object.hasOwn(edits, entry.bookingId) ? edits[entry.bookingId] : entry.attendance;
    if (value) answers[entry.bookingId] = value;
  }
  return answers;
}
